/** Pass-rate, colour and "where to focus" helpers for the Image Quality page. */
import { Cohort, CohortFilter, QualityTier } from './constants';
import {
  AssociateImageQuality,
  CheckFailureCount,
  PhotoCheckResult,
  PiiLabel,
  QualityThresholds,
} from './model';

/** Pass rate 0–100; null when nothing was captured (no rate to judge). */
export const passRatePct = (passing: number, total: number): number | null =>
  total > 0 ? (passing / total) * 100 : null;

/** "65.5%" — one decimal, trailing ".0" dropped ("33%"), "—" for no data. */
export const formatPct = (pct: number | null): string => {
  if (pct === null || !Number.isFinite(pct)) return '—';
  return `${Number(pct.toFixed(1))}%`;
};

/**
 * Colour band from the backend thresholds: ≥ target good, ≥ floor warn, else bad. No pass rate
 * (no photos) has no band.
 */
export function qualityTier(pct: number, thresholds: QualityThresholds): QualityTier;
export function qualityTier(pct: number | null, thresholds: QualityThresholds): QualityTier | null;
export function qualityTier(pct: number | null, thresholds: QualityThresholds): QualityTier | null {
  if (pct === null) return null;
  if (pct >= thresholds.targetPassRatePct) return 'good';
  if (pct >= thresholds.floorPassRatePct) return 'warn';
  return 'bad';
}

/** Share (0–100) of a part in a whole; 0 when the whole is 0. */
export const sharePct = (part: number, whole: number): number =>
  whole > 0 ? (part / whole) * 100 : 0;

/** The biggest failure driver and its share of all check failures. */
export interface LargestDriver {
  failureLabel: string;
  failedPhotos: number;
  /** % of all check failures (sum over checks). */
  sharePct: number;
}

export const largestDriver = (breakdown: CheckFailureCount[]): LargestDriver | null => {
  if (!breakdown.length) return null;
  const top = breakdown.reduce((largest, check) =>
    check.failedPhotos > largest.failedPhotos ? check : largest
  );
  if (top.failedPhotos === 0) return null;
  const total = breakdown.reduce((sum, c) => sum + c.failedPhotos, 0);
  return {
    failureLabel: top.failureLabel,
    failedPhotos: top.failedPhotos,
    sharePct: sharePct(top.failedPhotos, total),
  };
};

/** "2 of 3 wks <60%" — only when the associate meets the repeat bar. */
export const repeatBelowFloorLabel = (
  associate: AssociateImageQuality,
  thresholds: QualityThresholds
): string | null => {
  const weeks = associate.weeksBelowFloor;
  if (weeks === undefined || weeks < thresholds.repeatMinWeeks) return null;
  return `${weeks} of ${thresholds.repeatWindowWeeks} wks <${thresholds.floorPassRatePct}%`;
};

/** yyyy-MM-dd → "Sep 17" (calendar date; no timezone shift). */
export const formatOfdDate = (ofdDate: string): string => {
  const [y, m, d] = ofdDate.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
};

/** yyyy-MM-dd shifted by `days` (calendar arithmetic in UTC, so DST never skews it). */
export const addDays = (ofdDate: string, days: number): string => {
  const [y, m, d] = ofdDate.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

/** "Sep 14 – Oct 4" / "Oct 10". */
export const formatRangeLabel = (startDate: string, endDate: string): string => {
  if (startDate === endDate) return formatOfdDate(endDate);
  return `${formatOfdDate(startDate)} – ${formatOfdDate(endDate)}`;
};

const PII_LABEL_TEXT: Record<PiiLabel, string> = {
  FACE: 'face',
  PERSON: 'person',
  NAME: 'name',
  ADDRESS: 'address',
};

/** ['FACE', 'NAME'] → "Detected: face, name"; unknown labels pass through lower-cased. */
export const formatDetectedPii = (labels: string[]): string =>
  `Detected: ${labels.map(label => PII_LABEL_TEXT[label as PiiLabel] ?? label.toLowerCase()).join(', ')}`;

/** { score: 0.21, threshold: 0.35, passWhen: 'AT_LEAST' } → "0.21 (needs ≥ 0.35)"; null without a score. */
export const formatCheckScore = (check: PhotoCheckResult): string | null => {
  if (check.score === undefined) return null;
  if (check.threshold === undefined) return check.score.toFixed(2);
  // A score that rounds onto the cut-off (0.097 → "0.10") would contradict its ✗, so show 3 decimals.
  const value =
    check.score.toFixed(2) === check.threshold.toFixed(2)
      ? check.score.toFixed(3)
      : check.score.toFixed(2);
  const rule = check.passWhen === 'BELOW' ? '<' : '≥';
  return `${value} (needs ${rule} ${check.threshold.toFixed(2)})`;
};

/** How many associates the bottom `percent`% of `ranked` is: rounded up, at least one when anyone is ranked. */
export const bottomShareCount = (ranked: number, percent: number): number =>
  ranked > 0 ? Math.max(1, Math.ceil((ranked * percent) / 100)) : 0;

/** Cohort from the backend thresholds: top ≥ target, middle ≥ floor, bottom below floor; null without photos. */
export const associateCohort = (
  associate: AssociateImageQuality,
  thresholds: QualityThresholds
): Cohort | null => {
  const tier = qualityTier(passRatePct(associate.passingPhotos, associate.totalPhotos), thresholds);
  if (tier === null) return null;
  return tier === 'good' ? Cohort.TOP : tier === 'warn' ? Cohort.MIDDLE : Cohort.BOTTOM;
};

/** Chip labels with the live cut-offs, e.g. "Top (≥90%)", "Middle (60% to <90%)", "Bottom (<60%)". */
export const cohortLabel = (cohort: Cohort, thresholds: QualityThresholds): string => {
  const { targetPassRatePct: target, floorPassRatePct: floor } = thresholds;
  if (cohort === Cohort.TOP) return `Top (≥${target}%)`;
  if (cohort === Cohort.MIDDLE) return `Middle (${floor}% to <${target}%)`;
  return `Bottom (<${floor}%)`;
};

/** 12.3 → "12.3s"; "—" when not supplied. */
export const formatCycleTime = (seconds?: number): string =>
  seconds === undefined || !Number.isFinite(seconds) ? '—' : `${Number(seconds.toFixed(1))}s`;

/** The associates in a cohort ('' = everyone), order kept. */
export const filterByCohort = (
  associates: AssociateImageQuality[],
  thresholds: QualityThresholds,
  cohort: CohortFilter
): AssociateImageQuality[] =>
  cohort
    ? associates.filter(associate => associateCohort(associate, thresholds) === cohort)
    : associates;
